import {BreadcrumbsManager} from "../managers/breadcrumbs/breadcrumbs-manager";
import {WebsiteModuleState} from "../../apps/website/models/website-module.state";
import {BaseLinkModel} from "../../infrastructure/models/link/base-link-model";
import {action, computed} from "mobx";
import {AllScoresLinkModel} from "../../containers/all-scores-container/models/all-scores-container-link-model";
import {DASHBOARD} from "../../containers/dashboard-container/dashboard-container.consts";
import {GameCenterLinkModel} from "../../containers/game-center-container/models/game-center.link-model";
import {ANALYTICS} from "../../third-party-services/analytics/analytics-consts";
import {BaseHashParamsModel} from "../components/router/models/base-hash-params-model";
import {GameCenterHashParamsModel} from "../../containers/game-center-container/models/game-center.hash-params-model";
import {AppTerms} from "../app/app-terms";
import {AthleteLinkModel} from "../../containers/athlete-container/models/athlete-link-model";
import {RouterController} from "../components/router/router-controller";
import {ActionHandler} from "../../infrastructure/handlers/action-handler";
import type {BaseController} from "../../infrastructure/controllers/base-controller";
import {CompetitionLinkModel} from "../../containers/competition-container/models/competition.link-model";
import {CompetitorLinkModel} from "../../containers/competitor-container/models/competitor-link-model";
import {GameCenterLinkParams} from "../../containers/game-center-container/models/game-center-link-params";
import {NEW_COMPETITOR_CONTAINER} from "../../containers/competitor-container/competitor-container.consts";
import {COMPETITION_CONTAINER} from "../../containers/competition-container/competition-container.consts";

export class BreadcrumbsPopulateHandler extends ActionHandler {
  breadcrumbsManager: BreadcrumbsManager;
  websiteModel: WebsiteModuleState;

  static injectables = [BreadcrumbsManager, WebsiteModuleState, RouterController];

  constructor(breadcrumbsManager: BreadcrumbsManager, websiteModuleState: WebsiteModuleState, routerController: RouterController) {
    super();

    this.breadcrumbsManager = breadcrumbsManager;
    this.websiteModel = websiteModuleState;
    this.routerController = routerController;
  }

  onInitialized(controller: BaseController) {
    super.onInitialized(controller);

    this.cleanBreadcrumbs();
    this.breadcrumbsManager._showBreadcrumbs = true;
  }

  onLoaded() {
    super.onLoaded();

    this.cleanBreadcrumbs()
  }

  onUnloaded() {
    super.onUnloaded();

    this.breadcrumbsManager._showBreadcrumbs = false;
  }

  createBreadcrumbs(sportName) {
    this.cleanBreadcrumbs()
    this.addAllScoresBreadcrumbs(sportName)
  }

  addAllScoresBreadcrumbs(sportName) {
    let name = sportName;

    if (!sportName) {
      name = this.websiteModel.appState.context.sportType?.name;
    }

    this.breadcrumbs.add(name, this.allScoresLinkModel);
  }

  addGameCompetitionBreadcrumbs(entity) {
    this.breadcrumbs.add(entity?.competition.name, this.competitionLinkModel, ANALYTICS.ENTITY_TYPES.competition);
  }

  addGameBreadcrumbs(matchName) {
    this.breadcrumbs.add(matchName, this.gameCenterLinkModel, ANALYTICS.ENTITY_TYPES.game);
  }

  @action addEntityBreadcrumbs(name, linkType, filter, hashParams, entityType, metaDataIncluded): BaseLinkModel {
    const entityNameForURL: String = this.currentLinkModelParams.competition || this.currentLinkModelParams.competitor;
    const competitionExpandedParamsPages = [COMPETITION_CONTAINER.FILTERS.standings, COMPETITION_CONTAINER.FILTERS.matches, COMPETITION_CONTAINER.FILTERS.fixtures, COMPETITION_CONTAINER.FILTERS.results]
    const competitorExpandedParamsPages = [NEW_COMPETITOR_CONTAINER.FILTERS.standings, NEW_COMPETITOR_CONTAINER.FILTERS.brackets, NEW_COMPETITOR_CONTAINER.FILTERS.matches, NEW_COMPETITOR_CONTAINER.FILTERS.fixtures, NEW_COMPETITOR_CONTAINER.FILTERS.results]
    const expandedParamsPages = [competitionExpandedParamsPages, competitorExpandedParamsPages].flat();
    const isExpandedParams: Boolean = expandedParamsPages.includes(this.filter)
    const isFollow: Boolean = this.websiteModel.entitiesContext.competition?.isFollow || this.websiteModel.entitiesContext.competitor?.isFollow;

    const linkModel = new linkType(
      this.currentLinkModelParams.id,
      this.currentLinkModelParams.sportName,
      entityNameForURL,
      isExpandedParams ? filter : isFollow,
      hashParams,
      !isExpandedParams ? isFollow : filter,
    );

    this.breadcrumbs.add(name, linkModel, entityType, metaDataIncluded)
  }

  get breadcrumbs() {
    return this.websiteModel.breadcrumbsManager;
  }

  cleanBreadcrumbs() {
    this.websiteModel.breadcrumbsManager.clean();    //TODO: activate on route-change-handler
  }

  get terms(): AppTerms {
    return this.websiteModel.terms;
  }

  @computed get currentLinkModelParams(): Boolean {
    const sportName: String = this.websiteModel.currentLinkModel?.params.sportName;
    const id: String = this.websiteModel.currentLinkModel?.params.id;
    const country: String = this.websiteModel.currentLinkModel?.params.countryName;
    const competitionKey: String = this.websiteModel.currentLinkModel?.params.competitionKey;
    const matchDetails: String = this.websiteModel.currentLinkModel?.params.matchDetails;
    const competition: String = this.websiteModel.currentLinkModel?.params.competitionName;
    const competitor: String = this.websiteModel.currentLinkModel?.params.competitorName;
    const competitors: String = this.websiteModel.currentLinkModel?.params.competitorNames;
    const matchupId: String = this.websiteModel.currentLinkModel?.params.matchupId;
    const details: String = this.websiteModel.currentLinkModel?.params.details;
    const hashParams: BaseHashParamsModel = this.websiteModel.currentLinkModel?.hashParams;

    return {id, sportName, country, competition, competitor, competitors, matchupId, details, hashParams, competitionKey, matchDetails}
  }

  @computed get allScoresLinkModel(): AllScoresLinkModel {
    const sportName: String = this.currentLinkModelParams.sportName;
    const linkModel: AllScoresLinkModel = new AllScoresLinkModel(DASHBOARD.FILTERS.all, sportName);

    return linkModel;
  }

  @computed get competitionLinkModel(): CompetitionLinkModel {
    const competition = this.websiteModel.entitiesContext.competition || this.websiteModel.entitiesContext.game?.competition;

    if (competition?.nameForURL) {
      return new CompetitionLinkModel(
        competition.id,
        competition.sport.nameForURL,
        competition.nameForURL,
        competition.isFollow
      );
    }

    const values = this.currentLinkModelParams.competitionKey?.splitByLastOccurrence('-');
    const competitionNameFromUrl = this.currentLinkModelParams.competition || values?.first();
    const competitionName = competitionNameFromUrl !== 'undefined' ? competitionNameFromUrl : null;

    return new CompetitionLinkModel(
      values?.last(),
      this.currentLinkModelParams.sportName,
      competitionName
    );
  }

  @computed get competitorLinkModel(): CompetitorLinkModel {
    const linkModel = new CompetitorLinkModel(
      this.websiteModel.entitiesContext.competitor.id,
      this.currentLinkModelParams.sportName,
      this.currentLinkModelParams.competitor,
      this.websiteModel.entitiesContext.competitor.isFollow
    );

    return linkModel;
  }

  @computed get athleteLinkModel(): AthleteLinkModel {
    const values = this.currentLinkModelParams.details?.splitByLastOccurrence('-');
    const linkModel = new AthleteLinkModel(values?.last(), this.currentLinkModelParams.sportName, values?.first(), null, null, null, null, this.websiteModel?.entitiesContext?.athlete?.isFollow);

    return linkModel;
  }

  @computed get gameCenterLinkModel(): GameCenterLinkModel {
    const game = this.websiteModel.entitiesContext?.game;
    const hashParams = new GameCenterHashParamsModel({id: this.websiteModel.currentLinkModel?.hashParams?.id});

    if (game?.competition?.nameForURL) {
      return new GameCenterLinkModel(
        game.matchupId,
        game.sport.nameForURL,
        game.competition.nameForURL,
        game.nameForUrl,
        hashParams,
        game.isFollow
      );
    }

    return this.innerLinkModel(GameCenterLinkModel, GameCenterHashParamsModel);
  }

  innerLinkModel(linkType, hashType): BaseLinkModel {
    const params = new GameCenterLinkParams(this.currentLinkModelParams.competitionKey, this.currentLinkModelParams.matchDetails, this.currentLinkModelParams.sportName)
    const hashParams = new hashType({id: this.websiteModel.currentLinkModel?.hashParams?.id});
    const competitionName = params.competitionName !== 'undefined' ? params.competitionName : null;

    const linkModel = new linkType(
      params.matchupId,
      params.sportName,
      competitionName,
      params.competitorNames,
      hashParams
    );

    return linkModel;
  }

  _handleEntityChange(entity) {
    entity && this.createBreadcrumbs(entity)
  }
}
